export const LABEL_COLOR_PALETTE = [
  "#b60205",
  "#d93f0b",
  "#fbca04",
  "#0e8a16",
  "#006b75",
  "#1d76db",
  "#0052cc",
  "#5319e7",
  "#e99695",
  "#f9d0c4",
  "#fef2c0",
  "#c2e0c6",
  "#bfdadc",
  "#c5def5",
  "#bfd4f2",
  "#d4c5f9",
];

export const LABEL_SHUFFLE_COLORS = [
  ...LABEL_COLOR_PALETTE,
  "#7f1d1d",
  "#9a3412",
  "#b45309",
  "#a16207",
  "#3f6212",
  "#15803d",
  "#047857",
  "#0f766e",
  "#0e7490",
  "#0369a1",
  "#1d4ed8",
  "#4338ca",
  "#6d28d9",
  "#7e22ce",
  "#a21caf",
  "#be185d",
  "#881337",
  "#78350f",
  "#f97316",
  "#eab308",
  "#22c55e",
  "#06b6d4",
  "#3b82f6",
  "#8b5cf6",
  "#ec4899",
  "#f87171",
  "#57606a",
  "#6b7280",
  "#bfbfbf",
];

export function normalizeLabelColor(color: string): string {
  let hex = color.replace("#", "").toLowerCase();

  if (!/^[0-9a-f]{1,6}$/.test(hex)) {
    return "#bfbfbf";
  }

  if (hex.length === 3) {
    hex = hex
      .split("")
      .map((c) => {
        return c + c;
      })
      .join("");
  }

  return "#" + hex.padStart(6, "0");
}

function channelToLinear(value: number) {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(hex: string) {
  const r = channelToLinear(parseInt(hex.slice(1, 3), 16));
  const g = channelToLinear(parseInt(hex.slice(3, 5), 16));
  const b = channelToLinear(parseInt(hex.slice(5, 7), 16));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function contrastRatio(luminanceA: number, luminanceB: number) {
  const [lighter, darker] =
    luminanceA > luminanceB
      ? [luminanceA, luminanceB]
      : [luminanceB, luminanceA];
  return (lighter + 0.05) / (darker + 0.05);
}

function hexToHsl(hex: string) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;

  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return { h, s, l };
}

function hueToChannel(p: number, q: number, t: number) {
  let x = t;

  if (x < 0) x += 1;

  if (x > 1) x -= 1;

  if (x < 1 / 6) return p + (q - p) * 6 * x;

  if (x < 1 / 2) return q;

  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;

  return p;
}

function hslToHex(h: number, s: number, l: number) {
  let r: number;
  let g: number;
  let b: number;

  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToChannel(p, q, h + 1 / 3);
    g = hueToChannel(p, q, h);
    b = hueToChannel(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    return Math.round(c * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return "#" + toHex(r) + toHex(g) + toHex(b);
}

const AA_CONTRAST = 4.5;
const TARGET_CONTRAST = 6;

export function getLabelTextColor(backgroundColor: string): string {
  const background = normalizeLabelColor(backgroundColor);
  const backgroundLuminance = relativeLuminance(background);
  const useLightText = contrastRatio(1, backgroundLuminance) >= AA_CONTRAST;

  const { h, s } = hexToHsl(background);
  let { l } = hexToHsl(background);
  const step = useLightText ? 0.02 : -0.02;

  let candidate = useLightText ? "#ffffff" : "#000000";

  while (l > 0 && l < 1) {
    l = Math.min(1, Math.max(0, l + step));
    candidate = hslToHex(h, s, l);
    const ratio = contrastRatio(
      relativeLuminance(candidate),
      backgroundLuminance,
    );

    if (ratio >= TARGET_CONTRAST) {
      return candidate;
    }
  }

  return candidate;
}

export function randomLabelColor(): string {
  const index = Math.floor(Math.random() * LABEL_SHUFFLE_COLORS.length);
  return LABEL_SHUFFLE_COLORS[index];
}
